export function dateInTimeZone(
  date = new Date(),
  timeZone = "Asia/Manila"
): string {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).formatToParts(date)
  const values = Object.fromEntries(
    parts.map((part) => [part.type, part.value])
  )

  return `${values.year}-${values.month}-${values.day}`
}

export type CourseClock = {
  date: string
  minutes: number
}

export function courseClockInTimeZone(
  date = new Date(),
  timeZone = "Asia/Manila"
): CourseClock {
  const parts = new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).formatToParts(date)
  const values = Object.fromEntries(
    parts.map((part) => [part.type, part.value])
  )

  return {
    date: `${values.year}-${values.month}-${values.day}`,
    minutes: Number(values.hour) * 60 + Number(values.minute),
  }
}

export function isCourseTimeInFuture(
  date: string,
  startMinutes: number,
  clock: CourseClock
): boolean {
  return (
    date > clock.date || (date === clock.date && startMinutes > clock.minutes)
  )
}

export function addDays(date: string, amount: number): string {
  const [year, month, day] = date.split("-").map(Number)
  const value = new Date(Date.UTC(year, month - 1, day + amount))

  return value.toISOString().slice(0, 10)
}

export function isIsoDate(value: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return false
  }

  const [year, month, day] = value.split("-").map(Number)
  const parsed = new Date(Date.UTC(year, month - 1, day))

  return parsed.toISOString().slice(0, 10) === value
}

export function formatCourseDate(
  value: string,
  options: Intl.DateTimeFormatOptions = {}
): string {
  return new Intl.DateTimeFormat("en-PH", {
    weekday: "long",
    month: "long",
    day: "numeric",
    ...options,
    timeZone: "UTC",
  }).format(new Date(`${value}T12:00:00.000Z`))
}

export function formatShortDate(value: string): string {
  return formatCourseDate(value, {
    weekday: "short",
    month: "short",
  })
}

export function formatMinutes(minutes: number): string {
  const hours = Math.floor(minutes / 60)
  const remainder = minutes % 60
  const suffix = hours >= 12 ? "PM" : "AM"
  const displayHour = hours % 12 || 12

  return `${displayHour}:${remainder.toString().padStart(2, "0")} ${suffix}`
}

export function minutesToTimeInput(minutes: number): string {
  const normalized = Math.max(0, Math.min(1439, Math.trunc(minutes)))
  const hours = Math.floor(normalized / 60)
  const remainder = normalized % 60

  return `${hours.toString().padStart(2, "0")}:${remainder
    .toString()
    .padStart(2, "0")}`
}

export function timeInputToMinutes(value: string): number | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value)

  if (!match) {
    return null
  }

  return Number(match[1]) * 60 + Number(match[2])
}

export function formatCurrency(cents: number, currency = "PHP"): string {
  return new Intl.NumberFormat("en-PH", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(cents / 100)
}

export function formatTimestamp(
  value: number | string | Date,
  timeZone = "Asia/Manila"
): string {
  return new Intl.DateTimeFormat("en-PH", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone,
  }).format(new Date(value))
}
